import React, { useState } from "react";
import Layout from "../../Layout";
import "./project_case.css";

import Tensor_Banner from "../../Images/Tensor_Banner.png";
import Tensor_Video from "../../Videos/Tensor_Video.mp4";

export default function Tensor() {
  const [active, setActive] = useState("discover");

  function showDiscover() {
    setActive("discover");
  }
  function showWireframes() {
    setActive("wireframes");
  }
  function showDev() {
    setActive("dev");
  }
  function showFinal() {
    setActive("final");
  }

  return (
    <Layout>
      <main className="cs-page">
        {/* 1) FULL WIDTH HERO IMAGE */}
        <section className="cs-hero">
          <img src={Tensor_Banner} alt="Digital Handwritten Model Banner" />
        </section>

        {/* 2) TITLE / TAGLINE / META */}
        <section className="cs-block">
          <h1 className="cs-title">
            {" "}
            <u>TensorFlow Project</u>
          </h1>
          <p className="cs-tagline">
            A hands-on exploration of machine learning—training a TensorFlow
            model to understand handwritten patterns and improve over time.
          </p>

          <div className="cs-divider" />

          <div className="cs-meta">
            <div className="cs-meta-item">
              <h3 className="cs-meta-label">Timeline</h3>
              <p className="cs-meta-text">March 2024</p>
            </div>
            <div className="cs-meta-item">
              <h3 className="cs-meta-label">Programs Used</h3>
              <p className="cs-meta-text">
                {" "}
                Python, Tensorflow, Git + GitHub, Numpy, Matplotlib
              </p>
            </div>
          </div>
          <div className="cs-divider" />
        </section>

        {/* 3) ABOUT THE PROJECT */}
        <section className="cs-block">
          <h2 className="cs-h2">About the Project</h2>
          <p className="cs-tagline">
            The Digital Handwritten Model project was my first deep dive into
            machine learning, where I built a handwriting-recognition system
            using Python, TensorFlow, and a structured training pipeline. I
            trained the model on thousands of handwritten examples, learning how
            to preprocess data, tune hyperparameters, and measure accuracy
            across different stages. Along the way, I explored model evaluation,
            visualization tools, and the impact of training iterations on
            real-world predictions. This project helped me understand how raw
            input transforms into meaningful output, building confidence in my
            ability to design, test, and refine machine learning models from
            scratch. If needed, the model can later be extended for broader
            classification tasks or integrated into a user-facing app.
          </p>
        </section>

        <div className="cs-divider" />

        {/* 4) MY ROLE + ACCOMPLISHMENTS */}
        <section className="cs-block">
          <h2 className="cs-h2">My Role</h2>
          <p className="cs-tagline">
            I built, trained, and evaluated a TensorFlow model to recognize
            handwritten digits—handling the full machine-learning pipeline from
            data preprocessing to accuracy testing and refinement.
          </p>

          <div className="cs-card">
            <div class="cs-item">
              <span class="cs-item-title">Data Preparation & Processing:</span>
              <span class="cs-item-text">
                Cleaned and preprocessed handwritten image data, normalized
                inputs, and structured datasets to ensure consistent learning
                during training.
              </span>
            </div>

            <div class="cs-item">
              <span class="cs-item-title">Model Architecture Design:</span>
              <span class="cs-item-text">
                Built and tuned a TensorFlow neural network, experimenting with
                layers, activations, and optimization methods to improve
                accuracy.
              </span>
            </div>

            <div class="cs-item">
              <span class="cs-item-title">Training & Evaluation:</span>
              <span class="cs-item-text">
                Trained the model on thousands of handwriting samples, adjusted
                hyperparameters, and measured performance using validation and
                test datasets.
              </span>
            </div>

            <div class="cs-item">
              <span class="cs-item-title">Visualization & Analysis:</span>
              <span class="cs-item-text">
                Used tools such as Matplotlib and TensorFlow metrics to
                visualize training progress, diagnose issues, and understand
                model behavior.
              </span>
            </div>

            <div class="cs-item">
              <span class="cs-item-title">
                Documentation & Experiment Tracking:
              </span>
              <span class="cs-item-text">
                Documented model iterations, training results, and setup steps
                to support reproducibility and future improvements.
              </span>
            </div>
          </div>
        </section>

        <div className="cs-divider" />

        {/* 6) MY PROCESS: MENU (left) + PANEL (right) */}
        <section className="cs-block">
          <h2 className="cs-h2">My Process</h2>

          <div className="cs-process">
            <div className="cs-menu">
              <button
                className={
                  "cs-menu-item" + (active === "discover" ? " is-active" : "")
                }
                onMouseEnter={showDiscover}
              >
                Project Overview
              </button>

              <button
                className={
                  "cs-menu-item" + (active === "wireframes" ? " is-active" : "")
                }
                onMouseEnter={showWireframes}
              >
                Data Preparation
              </button>

              <button
                className={
                  "cs-menu-item" + (active === "dev" ? " is-active" : "")
                }
                onMouseEnter={showDev}
              >
                Training & Optimization
              </button>

              <button
                className={
                  "cs-menu-item" + (active === "final" ? " is-active" : "")
                }
                onMouseEnter={showFinal}
              >
                Results & Evaluation
              </button>
            </div>

            <div className="cs-panel">
              {/* PROJECT OVERVIEW */}
              <div
                className="cs-pane"
                style={{ display: active === "discover" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Project Overview</u>
                </h3>
                <p className="cs-tagline">
                  This project began as an exploration into machine learning and
                  pattern recognition using TensorFlow. The goal was to build a
                  model capable of interpreting handwritten inputs by learning
                  from thousands of labeled examples. I set out to understand
                  the full lifecycle of an ML project—from structuring the
                  dataset to shaping a neural network that can generalize beyond
                  the training data.
                </p>

                <p className="cs-tagline">
                  Before writing any code, I reviewed the problem requirements,
                  studied the MNIST dataset structure, and outlined the steps
                  needed for ingestion, preprocessing, training, and evaluation.
                  This planning helped ensure that every technical decision—from
                  model architecture to training strategy—was aligned with the
                  project’s learning objectives.
                </p>
              </div>

              {/* DATA PREPARATION */}
              <div
                className="cs-pane"
                style={{ display: active === "wireframes" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Data Preparation</u>
                </h3>
                <p className="cs-tagline">
                  The first technical step was preparing the dataset. I
                  normalized pixel values, reshaped image tensors, and converted
                  labels into formats suitable for training. This ensured the
                  model received clean, standardized inputs that improved
                  learning efficiency.
                </p>

                <p className="cs-tagline">
                  I also split the dataset into training and testing sets,
                  allowing me to track real performance rather than rely solely
                  on training accuracy. Proper preprocessing was
                  essential—without it, even a well-designed model would
                  struggle to recognize patterns consistently.
                </p>
              </div>

              {/* TRAINING & OPTIMIZATION */}
              <div
                className="cs-pane"
                style={{ display: active === "dev" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Training & Optimization</u>
                </h3>
                <p className="cs-tagline">
                  I built and trained a neural network using TensorFlow and
                  Keras, selecting layers that excel at image recognition such
                  as dense layers with ReLU activation and a softmax output for
                  classification. During training, I monitored accuracy, loss,
                  and learning behavior to tune hyperparameters like batch size,
                  learning rate, and epoch count.
                </p>

                <p className="cs-tagline">
                  Through iterative experimentation, I refined the model to
                  reduce overfitting and improve generalization. I also
                  incorporated activation functions and optimization techniques
                  that helped the model converge more efficiently. Each training
                  cycle provided insights into how adjustments in architecture
                  or preprocessing directly impacted performance.
                </p>
              </div>

              {/* RESULTS & EVALUATION */}
              <div
                className="cs-pane"
                style={{ display: active === "final" ? "block" : "none" }}
              >
                <h3 className="cs-pane-title">
                  <u>Results & Evaluation</u>
                </h3>
                <p className="cs-tagline">
                  The final model achieved strong accuracy on unseen handwritten
                  samples, demonstrating successful learning and robust pattern
                  recognition. Testing showed that the network could correctly
                  classify most digits, even when handwriting styles varied
                  significantly from the examples it was trained on.
                </p>

                <p className="cs-tagline">
                  This project gave me hands-on experience with the full machine
                  learning pipeline and strengthened my understanding of
                  TensorFlow, neural networks, and model evaluation. It taught
                  me how data preparation, architecture design, and training
                  strategy must work together to create a functional AI model.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="cs-divider" />

        <section className="cs-block">
                  <h2 className="cs-h2">Video Demonstration</h2>
                  <div className="cs-video">

                    <video src={Tensor_Video} controls preload="none" muted />
                  </div>
                </section>

        {/* 7) Learning & Impact */}
        <section className="cs-block">
          <h2 className="cs-h2">Learning & Impact</h2>

          <p className="cs-tagline">
            Building this handwritten recognition model deepened my
            understanding of machine learning fundamentals—from dataset
            preparation to neural network architecture design. It strengthened
            my ability to make data-driven decisions, iterate on model
            performance, and evaluate results with a critical engineering
            mindset.
          </p>

          <div className="cs-card">
            <div className="cs-item">
              <span className="cs-item-title">
                TensorFlow Model Architecture:
              </span>
              <span className="cs-item-text">
                Learned how to build and structure neural networks using
                TensorFlow and Keras, selecting activation functions, layers,
                and optimizers that fit the problem.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">Data Preprocessing:</span>
              <span className="cs-item-text">
                Gained experience cleaning, normalizing, and reshaping image
                data, which directly improved training efficiency and model
                accuracy.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">Model Training & Tuning:</span>
              <span className="cs-item-text">
                Learned how to monitor accuracy, overfitting, and loss curves to
                adjust hyperparameters and improve generalization.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">Evaluation & Testing:</span>
              <span className="cs-item-text">
                Strengthened my understanding of test datasets, performance
                metrics, and how to validate whether a model genuinely learned
                patterns.
              </span>
            </div>

            <div className="cs-item">
              <span className="cs-item-title">Problem-Solving Mindset:</span>
              <span className="cs-item-text">
                Developed the ability to approach ML challenges
                methodically—diagnosing poor results, experimenting with
                improvements, and iterating with purpose.
              </span>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
}
